import { API_BASE_URL } from './api';
import { authService } from './authService';
import { Post } from './postService';
import { DmMessage } from './messageService';

const authJsonHeaders = () => ({ 'Content-Type': 'application/json', ...authService.getAuthHeaders() });

export interface StoriesResponse {
  /** Live stories from you, your buddies, and people you follow — oldest first (the order they're watched in). */
  stories: Post[];
  /** Ids among `stories` you have already watched, on any device. */
  seenStoryIds: string[];
  /** View counts for your OWN stories only. */
  viewCounts: Record<string, number>;
}

export interface StoryViewer {
  userId: string;
  displayName: string;
  profilePhotoUrl?: string | null;
  viewedAt: string;
  reaction?: string | null;
}

/** The one-tap set — keep in sync with StoryReactions.Allowed on the API. */
export const STORY_REACTIONS = ['❤️', '🔥', '👏', '😮', '🤿', '🐠'] as const;

// A story is a 24-hour photo/video moment shown only to buddies/followers. It never appears in the
// feed, on a profile, or in any trending/community list — only through these calls.
export const storyService = {
  list: async (): Promise<StoriesResponse> => {
    const res = await fetch(`${API_BASE_URL}/stories`, { headers: { ...authService.getAuthHeaders() } });
    if (!res.ok) throw new Error('Failed to load stories');
    return res.json();
  },

  create: async (request: { mediaKey: string; caption?: string; visibility: 'Buddies' | 'Followers' }): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/stories`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error((await res.text()) || 'Failed to share story');
    return res.json();
  },

  remove: async (storyId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/stories/${encodeURIComponent(storyId)}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to delete story');
  },

  /** Tells the server you watched this story. Idempotent; silently ignored for your own stories. */
  recordView: async (storyId: string): Promise<void> => {
    await fetch(`${API_BASE_URL}/stories/${encodeURIComponent(storyId)}/view`, {
      method: 'POST',
      headers: { ...authService.getAuthHeaders() },
    });
  },

  /** Author only: who has watched this story, newest first. */
  viewers: async (storyId: string): Promise<StoryViewer[]> => {
    const res = await fetch(`${API_BASE_URL}/stories/${encodeURIComponent(storyId)}/viewers`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load viewers');
    return res.json();
  },

  /** Replies go into your existing DM thread with the author, quoting the story. */
  reply: async (storyId: string, request: { text?: string; reaction?: string }): Promise<DmMessage> => {
    const res = await fetch(`${API_BASE_URL}/stories/${encodeURIComponent(storyId)}/reply`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error((await res.text()) || 'Failed to send reply');
    return res.json();
  },
};
